import { FC, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Box, Collapse, Stack } from '@chakra-ui/react';

import useCompanyContext from 'common/hooks/useCompanyContext';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import { StepProps } from 'components/Stepper/types';
import useToast from 'components/ToastProvider/useToast';
import { UploadedFileInfo } from 'components/Upload/types';
import PageHeader from 'features/Payments/Pages/Transfers/Pages/BulkTransfers/components/PageHeader';
import * as commonStyle from 'features/Payments/Pages/Transfers/Pages/BulkTransfers/styles';
import * as ContentLayout from 'layouts/ContentLayout';

import { useBulkTransferContext } from '../../context';
import { BulkTransferSharedState } from '../../types';
import ErrorDetailsBox from './components/ErrorDetailsBox/index';
import FileUpload from './components/FileUpload';
import TemplateDownloadRow from './components/TemplateDownloadRow';
import useComposeErrorMessages from './hooks/useComposeErrorMessages';
import { GetBulkTransferFileParseResultDocument } from './hooks/useGetBulkTransfersFileParseResult/getBulkTransferFileParseResult.gql';
import locales from './locales';

const TEMPLATE_URLS = {
  CSV: 'https://storage.googleapis.com/shine-public/web-app/bulk-transfer-templates/template.csv',
  XML: 'https://storage.googleapis.com/shine-public/web-app/bulk-transfer-templates/template.xml',
};

const BulkTransfersFileUpload: FC<StepProps> = ({ goNext }) => {
  const { companyUserId } = useCompanyContext();

  const { setSharedState } = useBulkTransferContext();
  const { showToast } = useToast();

  const [isUploading, setIsUploading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [getBulkTransferFileParseResult, { data, error, loading }] =
    useLazyQuery(GetBulkTransferFileParseResultDocument);

  const { errorMessages } = useComposeErrorMessages(
    data?.bulkBankTransferFileParseResult,
  );

  const onSuccessfulUpload = async (uploadedFile: UploadedFileInfo) => {
    const { bucketName, fileRef } = uploadedFile;
    if (companyUserId === undefined) {
      throw new Error('companyUserId is undefined in BulkTransfersFileUpload');
    }
    await getBulkTransferFileParseResult({
      variables: { input: { bucketName, companyUserId, fileRef } },
    });
  };

  useEffect((): (() => void) => {
    if (error) {
      showToast({ message: locales.uploadError, type: 'error' });
      // Returns an empty function to maintain a consistent return type.
      return () => {};
    }
    if (loading || data === undefined || errorMessages.length > 0) {
      // Returns an empty function to maintain a consistent return type.
      return () => {};
    }
    const { bucketName, fileRef, nextBalance, totalAmount, transfers } =
      data.bulkBankTransferFileParseResult;
    setIsSuccess(true);

    // Show the success message for a bit before going to the next step.
    const timeout = setTimeout(() => {
      setSharedState((prevState: BulkTransferSharedState) => ({
        ...prevState,
        bucketName,
        fileRef,
        nextBalance,
        totalAmount,
        transfers,
      }));
      goNext();
    }, 1000);

    return () => clearTimeout(timeout);
  }, [
    data,
    goNext,
    loading,
    setSharedState,
    showToast,
    errorMessages.length,
    error,
    isSuccess,
  ]);

  // keep showing hints that the file is uploading until the file is parsed
  useEffect(() => {
    setIsUploading(loading);
  }, [loading]);

  return (
    <ContentLayout.Root>
      <ContentLayout.Body>
        <commonStyle.NavigationHeaderWrapper>
          <BackButton />
        </commonStyle.NavigationHeaderWrapper>
        <Box marginTop="space-16">
          <PageHeader title={locales.pageTitle} />
        </Box>
        <Stack spacing="space-16">
          <Collapse animateOpacity in={!isSuccess}>
            <Stack spacing="space-16">
              <Text fontWeight="weight-500" variant="primary">
                {locales.title}
              </Text>
              <Text size="sm">{locales.description}</Text>
            </Stack>
          </Collapse>
          <FileUpload
            isLoading={isUploading}
            isSuccess={isSuccess}
            onSuccessfulUpload={onSuccessfulUpload}
            setIsUploading={setIsUploading}
          />
          <Collapse
            animateOpacity
            in={errorMessages.length > 0 && !isUploading}
          >
            <ErrorDetailsBox errorMessages={errorMessages} />
          </Collapse>
        </Stack>
        <Collapse animateOpacity in={!isSuccess}>
          <Box marginTop="space-56">
            <Box marginBottom="space-8">
              <Text size="sm" variant="light">
                {locales.actionCellsLabel}
              </Text>
            </Box>

            <SunshineCard.Group>
              <TemplateDownloadRow
                description={locales.csvDescription}
                downloadUrl={TEMPLATE_URLS.CSV}
                fileTypeLabel="CSV"
              />
              <TemplateDownloadRow
                description={locales.xmlDescription}
                downloadUrl={TEMPLATE_URLS.XML}
                fileTypeLabel="XML"
              />
            </SunshineCard.Group>
          </Box>
        </Collapse>
      </ContentLayout.Body>
    </ContentLayout.Root>
  );
};

export default BulkTransfersFileUpload;
